import { useApiContext } from '@/contexts/apiContext';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import { Box, Button, Stack, alpha, useTheme } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import RadioGroup from '../../raidioGroup';
import SelectField from '../../selectField';
import TextField from '../../textField';

type Props = {
  questions: Record<string, any>;
  userResponses: Record<string, any>;
  maxHeight: number;
  onUserResponseChange: (questionId: string, response: any) => void;
  onBack: () => void;
  onCancel: () => void;
  onNext: () => void;
};

const StepLlm = ({
  questions = {},
  userResponses = {},
  maxHeight,
  onUserResponseChange,
  onBack,
  onCancel,
  onNext,
}: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();
  const theme = useTheme();

  const [containerRef, containerSize] = useElementSize();
  const [actionPanelRef, actionPanelSize] = useElementSize();

  const [llms, setLlms] = useState<any[]>([]);
  const [models, setModels] = useState<any[]>([]);
  const [embeddingModels, setEmbeddingModels] = useState<any[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);

  useEffect(() => {
    const getLlms = async () => {
      try {
        setLoading(true);
        const response =
          userResponses?.mainRequirements?.[0] === 'qna'
            ? await apiContext.getConversationLlms()
            : await apiContext.getDataExtractionLlms();

        setLlms(response.map((llm) => ({ label: t(`common.llm.${llm}`), value: llm })));
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    getLlms();
  }, []);

  useEffect(() => {
    const getModels = async () => {
      try {
        setLoading(true);
        const response =
          userResponses?.mainRequirements?.[0] === 'qna'
            ? await apiContext.getConversationLlmModels(userResponses.llmSettings?.LLM_NAME)
            : await apiContext.getDataExtractionLlmModels(userResponses.llmSettings?.LLM_NAME);

        setModels(response.map((model) => ({ value: model.modelCode, label: model.modelName })));
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };
    const getEmbeddingModels = async () => {
      try {
        setLoading(true);
        const response = await apiContext.getEmbeddingsModels(userResponses.llmSettings?.LLM_NAME);

        setEmbeddingModels(response.map((model) => ({ value: model.modelCode, label: model.modelName })));
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    if (userResponses.llmSettings?.LLM_NAME) {
      getModels();
      if (userResponses?.mainRequirements?.[0] === 'qna') {
        getEmbeddingModels();
      }
    }
  }, [userResponses.llmSettings?.LLM_NAME]);

  const [errors, setErrors] = useState<any>({});

  const setLlmOption = (response: string) => {
    onUserResponseChange('llmOption', response);
  };

  const setLlmSettings = (settingName: string, value: any) => {
    onUserResponseChange('llmSettings', { ...userResponses.llmSettings, [settingName]: value });
  };

  const handleNext = () => {
    const errors: any = {};
    let hasError = false;
    if (!userResponses.llmOption) {
      errors.llmOption = t('common.errors.requiredField');
      hasError = true;
    }
    if (userResponses.llmOption === 'custom') {
      if (!userResponses.llmSettings?.LLM_NAME) {
        errors.LLM_NAME = t('common.errors.requiredField');
        hasError = true;
      }
      if (userResponses.llmSettings?.LLM_NAME === 'openai') {
        if (!userResponses.llmSettings?.LLM_OPENAI_MODEL) {
          errors.LLM_OPENAI_MODEL = t('common.errors.requiredField');
          hasError = true;
        }
        if (!userResponses.llmSettings?.LLM_OPENAI_API_KEY) {
          errors.LLM_OPENAI_API_KEY = t('common.errors.requiredField');
          hasError = true;
        }
        if (!userResponses.llmSettings?.LLM_OPENAI_API_PROJECT) {
          errors.LLM_OPENAI_API_PROJECT = t('common.errors.requiredField');
          hasError = true;
        }
        if (!userResponses.llmSettings?.LLM_OPENAI_API_ORGANISATION) {
          errors.LLM_OPENAI_API_ORGANISATION = t('common.errors.requiredField');
          hasError = true;
        }
      }
      if (userResponses.llmSettings?.LLM_NAME === 'azureopenai') {
        if (!userResponses.llmSettings?.LLM_AZURE_OPENAI_MODEL) {
          errors.LLM_AZURE_OPENAI_MODEL = t('common.errors.requiredField');
          hasError = true;
        }
        if (!userResponses.llmSettings?.LLM_AZURE_OPENAI_API_ENDPOINT) {
          errors.LLM_AZURE_OPENAI_API_ENDPOINT = t('common.errors.requiredField');
          hasError = true;
        }
        if (!userResponses.llmSettings?.LLM_AZURE_OPENAI_DEPLOYMENT_NAME) {
          errors.LLM_AZURE_OPENAI_DEPLOYMENT_NAME = t('common.errors.requiredField');
          hasError = true;
        }
        if (!userResponses.llmSettings?.LLM_AZURE_OPENAI_API_KEY) {
          errors.LLM_AZURE_OPENAI_API_KEY = t('common.errors.requiredField');
          hasError = true;
        }
      }
      if (userResponses.llmSettings?.LLM_NAME === 'geminiai') {
        if (!userResponses.llmSettings?.LLM_GEMINIAI_MODEL) {
          errors.LLM_GEMINIAI_MODEL = t('common.errors.requiredField');
          hasError = true;
        }
        if (!userResponses.llmSettings?.LLM_GEMINIAI_API_KEY) {
          errors.LLM_GEMINIAI_API_KEY = t('common.errors.requiredField');
          hasError = true;
        }
      }
      if (userResponses.llmSettings?.LLM_NAME === 'claudeai') {
        if (!userResponses.llmSettings?.LLM_CLAUDEAI_MODEL) {
          errors.LLM_CLAUDEAI_MODEL = t('common.errors.requiredField');
          hasError = true;
        }
        if (!userResponses.llmSettings?.LLM_CLAUDEAI_API_KEY) {
          errors.LLM_CLAUDEAI_API_KEY = t('common.errors.requiredField');
          hasError = true;
        }
      }
    }

    setErrors(errors);

    if (!hasError) {
      onNext();
    }
  };

  const handleCancel = () => {
    onCancel();
  };

  const handleBack = () => {
    onBack();
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        height: `${maxHeight}px`,
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderLeft: `3px solid ${theme.palette.secondary.main}`,
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
        <Stack alignItems={'flex-start'} spacing={1}>
          <RadioGroup
            label={questions?.llmOption?.question || 'Which LLM model engine would you like to use?'}
            value={userResponses.llmOption || 'builtIn'}
            options={
              questions?.llmOption?.options || [
                { label: 'Use built-in free tier (limited use)', value: 'builtIn' },
                { label: 'Provide your own custom LLM settings', value: 'custom' },
              ]
            }
            required
            error={errors.llmOption}
            fullWidth={false}
            onChange={setLlmOption}
          />
          {userResponses.llmOption === 'custom' && (
            <SelectField
              label={questions?.llmSettings?.LLM_NAME?.question || 'LLM Provider / Engine'}
              value={userResponses.llmSettings?.LLM_NAME}
              options={llms}
              required
              error={errors.LLM_NAME}
              onChange={(value) => setLlmSettings('LLM_NAME', value)}
              fullWidth={false}
            />
          )}
          {userResponses.llmSettings?.LLM_NAME === 'openai' && (
            <>
              <SelectField
                label={questions?.llmSettings?.LLM_OPENAI_MODEL?.question || 'OpenAI Model'}
                value={userResponses.llmSettings?.LLM_OPENAI_MODEL}
                options={models}
                required
                error={errors.LLM_OPENAI_MODEL}
                onChange={(value) => setLlmSettings('LLM_OPENAI_MODEL', value)}
                fullWidth={false}
              />
              {userResponses?.mainRequirements?.[0] === 'qna' && (
                <SelectField
                  label={questions?.llmSettings?.LLM_OPENAI_EMBEDDINGS_MODEL?.question || 'Embeddings Model'}
                  value={userResponses.llmSettings?.LLM_OPENAI_EMBEDDINGS_MODEL}
                  options={embeddingModels}
                  required
                  error={errors.LLM_OPENAI_EMBEDDINGS_MODEL}
                  onChange={(value) => setLlmSettings('LLM_OPENAI_EMBEDDINGS_MODEL', value)}
                  fullWidth={false}
                />
              )}
              <TextField
                label={questions?.llmSettings?.LLM_OPENAI_API_KEY?.question || 'OpenAI API Key'}
                value={userResponses.llmSettings?.LLM_OPENAI_API_KEY}
                required
                error={errors.LLM_OPENAI_API_KEY}
                onChange={(value) => setLlmSettings('LLM_OPENAI_API_KEY', value)}
                type="password"
                fullWidth={false}
              />
              <TextField
                label={questions?.llmSettings?.LLM_OPENAI_API_PROJECT?.question || 'API Project'}
                value={userResponses.llmSettings?.LLM_OPENAI_API_PROJECT}
                required
                error={errors.LLM_OPENAI_API_PROJECT}
                onChange={(value) => setLlmSettings('LLM_OPENAI_API_PROJECT', value)}
                fullWidth={false}
              />
              <TextField
                label={questions?.llmSettings?.LLM_OPENAI_API_ORGANISATION?.question || 'API Organisation'}
                value={userResponses.llmSettings?.LLM_OPENAI_API_ORGANISATION}
                required
                error={errors.LLM_OPENAI_API_ORGANISATION}
                onChange={(value) => setLlmSettings('LLM_OPENAI_API_ORGANISATION', value)}
                fullWidth={false}
              />
            </>
          )}
          {userResponses.llmSettings?.LLM_NAME === 'azureopenai' && (
            <>
              <SelectField
                label={questions?.llmSettings?.LLM_AZURE_OPENAI_MODEL?.question || 'Azure OpenAI Model'}
                value={userResponses.llmSettings?.LLM_AZURE_OPENAI_MODEL}
                options={models}
                required
                error={errors.LLM_AZURE_OPENAI_MODEL}
                onChange={(value) => setLlmSettings('LLM_AZURE_OPENAI_MODEL', value)}
                fullWidth={false}
              />
              {userResponses?.mainRequirements?.[0] === 'qna' && (
                <SelectField
                  label={questions?.llmSettings?.LLM_AZURE_OPENAI_EMBEDDINGS_MODEL?.question || 'Azure Embeddings Model'}
                  value={userResponses.llmSettings?.LLM_AZURE_OPENAI_EMBEDDINGS_MODEL}
                  options={embeddingModels}
                  required
                  error={errors.LLM_AZURE_OPENAI_EMBEDDINGS_MODEL}
                  onChange={(value) => setLlmSettings('LLM_AZURE_OPENAI_EMBEDDINGS_MODEL', value)}
                  fullWidth={false}
                />
              )}
              <TextField
                label={questions?.llmSettings?.LLM_AZURE_OPENAI_API_ENDPOINT?.question || 'Azure Endpoint'}
                value={userResponses.llmSettings?.LLM_AZURE_OPENAI_API_ENDPOINT}
                required
                error={errors.LLM_AZURE_OPENAI_API_ENDPOINT}
                onChange={(value) => setLlmSettings('LLM_AZURE_OPENAI_API_ENDPOINT', value)}
                fullWidth={false}
              />
              <TextField
                label={questions?.llmSettings?.LLM_AZURE_OPENAI_DEPLOYMENT_NAME?.question || 'Deployment Name'}
                value={userResponses.llmSettings?.LLM_AZURE_OPENAI_DEPLOYMENT_NAME}
                required
                error={errors.LLM_AZURE_OPENAI_DEPLOYMENT_NAME}
                onChange={(value) => setLlmSettings('LLM_AZURE_OPENAI_DEPLOYMENT_NAME', value)}
                fullWidth={false}
              />
              <TextField
                label={questions?.llmSettings?.LLM_AZURE_OPENAI_API_KEY?.question || 'Azure API Key'}
                value={userResponses.llmSettings?.LLM_AZURE_OPENAI_API_KEY}
                required
                error={errors.LLM_AZURE_OPENAI_API_KEY}
                onChange={(value) => setLlmSettings('LLM_AZURE_OPENAI_API_KEY', value)}
                type="password"
                fullWidth={false}
              />
            </>
          )}
          {userResponses.llmSettings?.LLM_NAME === 'geminiai' && (
            <>
              <SelectField
                label={questions?.llmSettings?.LLM_GEMINIAI_MODEL?.question || 'Gemini Model'}
                value={userResponses.llmSettings?.LLM_GEMINIAI_MODEL}
                options={models}
                required
                error={errors.LLM_GEMINIAI_MODEL}
                onChange={(value) => setLlmSettings('LLM_GEMINIAI_MODEL', value)}
                fullWidth={false}
              />
              {userResponses?.mainRequirements?.[0] === 'qna' && (
                <SelectField
                  label={questions?.llmSettings?.LLM_GEMINIAI_EMBEDDINGS_MODEL?.question || 'Gemini Embeddings Model'}
                  value={userResponses.llmSettings?.LLM_GEMINIAI_EMBEDDINGS_MODEL}
                  options={embeddingModels}
                  required
                  error={errors.LLM_GEMINIAI_EMBEDDINGS_MODEL}
                  onChange={(value) => setLlmSettings('LLM_GEMINIAI_EMBEDDINGS_MODEL', value)}
                  fullWidth={false}
                />
              )}
              <TextField
                label={questions?.llmSettings?.LLM_GEMINIAI_API_KEY?.question || 'Gemini API Key'}
                value={userResponses.llmSettings?.LLM_GEMINIAI_API_KEY}
                required
                error={errors.LLM_GEMINIAI_API_KEY}
                onChange={(value) => setLlmSettings('LLM_GEMINIAI_API_KEY', value)}
                type="password"
                fullWidth={false}
              />
            </>
          )}
          {userResponses.llmSettings?.LLM_NAME === 'claudeai' && (
            <>
              <SelectField
                label={questions?.llmSettings?.LLM_CLAUDEAI_MODEL?.question || 'Claude Model'}
                value={userResponses.llmSettings?.LLM_CLAUDEAI_MODEL}
                options={models}
                required
                error={errors.LLM_CLAUDEAI_MODEL}
                onChange={(value) => setLlmSettings('LLM_CLAUDEAI_MODEL', value)}
                fullWidth={false}
              />
              <TextField
                label={questions?.llmSettings?.LLM_CLAUDEAI_API_KEY?.question || 'Claude API Key'}
                value={userResponses.llmSettings?.LLM_CLAUDEAI_API_KEY}
                required
                error={errors.LLM_CLAUDEAI_API_KEY}
                onChange={(value) => setLlmSettings('LLM_CLAUDEAI_API_KEY', value)}
                type="password"
                fullWidth={false}
              />
            </>
          )}
        </Stack>
      </Box>
      <Box
        ref={actionPanelRef}
        sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.02) }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
          >
            {t('common.buttons.back')}
          </Button>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="text"
              onClick={handleCancel}
              sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
            >
              {t('common.buttons.cancel')}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              endIcon={<ArrowForwardIcon />}
              onClick={handleNext}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: '10px',
                px: 2.5,
                color: 'text.primary',
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              {t('common.buttons.next')}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default StepLlm;
